import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import { invitationQuery, useAcceptInvitation } from '@/features/workspace/api';
import { ensureNamespace } from '@/lib/i18n';

/**
 * The page a workspace invitation opens.
 *
 * It is reachable signed out, because the invitee may have no account at all —
 * which is the common case, and the whole reason the invitation exists. What it
 * cannot do is sign anybody in: an invitation sits in an inbox for a week, and a
 * link that both proved an address and opened a session would be a week-long
 * key to whatever account already uses that address.
 *
 * So there are three states, and the page's job is to tell them apart plainly:
 * signed in as the right person (join), signed in as somebody else (say so),
 * signed out (send them to sign in and come back).
 */
export const Route = createFileRoute('/invitation/$token')({
  beforeLoad: () => ensureNamespace('account'),
  component: InvitationPage,
});

function InvitationPage() {
  const { token } = Route.useParams();
  const { t } = useTranslation(['account', 'common']);
  const invitation = useQuery(invitationQuery(token));
  const user = useCurrentUser();
  const accept = useAcceptInvitation(token);

  if (invitation.isPending || user.isPending) {
    return <Shell>{t('common:status.loading')}</Shell>;
  }

  // Expired, spent and never-issued all read the same, as they do from the API.
  if (invitation.isError || !invitation.data) {
    return (
      <Shell>
        <p
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {t('account:invitation.invalid')}
        </p>
        <Link to="/login" className={linkBtn}>
          {t('account:invitation.toLogin')}
        </Link>
      </Shell>
    );
  }

  const { ownerName, email } = invitation.data;

  if (accept.isSuccess) {
    return (
      <Shell>
        <p className="text-sm text-neutral-700">{t('account:invitation.joined', { ownerName })}</p>
        {/* A full load: they may have signed in moments ago and the app's
            caches were filled for a different state. */}
        <a href="/app" className={primaryBtn}>
          {t('account:invitation.openApp')}
        </a>
      </Shell>
    );
  }

  const signedIn = user.data ?? null;

  return (
    <Shell>
      <h1 className="text-2xl font-semibold">{t('account:invitation.title', { ownerName })}</h1>
      <p className="text-sm text-neutral-600">{t('account:invitation.body', { ownerName })}</p>
      <p className="text-sm text-neutral-600">{t('account:invitation.keepsOwn')}</p>

      {!signedIn ? (
        <>
          <p className="text-sm text-neutral-700">
            {t('account:invitation.signInFirst', { email })}
          </p>
          {/* returnTo brings them back here once they have a session, including
              after the email-link route creates the account. */}
          <Link
            to="/login"
            search={{ returnTo: `/invitation/${token}`, email }}
            className={primaryBtn}
          >
            {t('account:invitation.signIn')}
          </Link>
        </>
      ) : signedIn.email.toLowerCase() !== email.toLowerCase() ? (
        <>
          <p
            role="alert"
            className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          >
            {t('account:invitation.wrongAccount', { invited: email, current: signedIn.email })}
          </p>
          <a href="/app/settings" className={linkBtn}>
            {t('account:invitation.switchAccount')}
          </a>
        </>
      ) : (
        <>
          {accept.isError ? (
            <p role="alert" className="text-sm text-red-600">
              {t('account:invitation.failed')}
            </p>
          ) : null}
          <button
            type="button"
            disabled={accept.isPending}
            onClick={() => accept.mutate()}
            className={primaryBtn}
          >
            {accept.isPending ? t('common:status.loading') : t('account:invitation.join')}
          </button>
        </>
      )}
    </Shell>
  );
}

const primaryBtn =
  'flex min-h-12 items-center justify-center rounded bg-neutral-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-40';
const linkBtn =
  'flex min-h-12 items-center justify-center rounded border border-neutral-300 bg-white px-4 py-2 text-sm font-medium';

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-full max-w-sm flex-col justify-center gap-5 px-4">
      {children}
    </main>
  );
}
